import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { completeProfilePath, soccerPath } from '../utils/paths';
import { SITE_APPS, SITE_NAME } from '../config/siteBranding';
import { SOCCER_LANDING_IMAGES } from '../config/soccerLandingAssets';
import PlayerHomeDashboard from './PlayerHomeDashboard';
import TrainingTodayCard from './TrainingTodayCard';
import './SoccerLanding.css';

const SoccerLanding = () => {
  const { isAuthenticated, isAdmin, isClubCreator, user } = useAuth();
  const [hasApprovedPlayer, setHasApprovedPlayer] = useState(false);
  const [hasPendingPlayer, setHasPendingPlayer] = useState(false);
  const [checkingPlayer, setCheckingPlayer] = useState(true);

  useEffect(() => {
    const checkPlayerStatus = async () => {
      if (!isAuthenticated || !user) {
        setCheckingPlayer(false);
        return;
      }

      try {
        const approvedPlayers = await api.getAllPlayers(user?.id);
        const approvedPlayer = approvedPlayers.find((p) => p.userId === user.id);
        setHasApprovedPlayer(!!approvedPlayer);

        if (!approvedPlayer) {
          try {
            const pendingPlayers = await api.getPendingPlayers();
            const pendingPlayer = pendingPlayers.find((p) => p.userId === user.id);
            setHasPendingPlayer(!!pendingPlayer);
          } catch {
            setHasPendingPlayer(false);
          }
        } else {
          setHasPendingPlayer(false);
        }
      } catch {
        setHasApprovedPlayer(false);
        setHasPendingPlayer(false);
      } finally {
        setCheckingPlayer(false);
      }
    };

    checkPlayerStatus();
  }, [isAuthenticated, user]);

  const canRegisterClub = isAdmin() || isClubCreator();
  const showQuickLinks = isAuthenticated && (hasApprovedPlayer || isAdmin() || isClubCreator());

  return (
    <div className="soccer-landing" aria-label={`${SITE_APPS.soccer.name} home`}>
      <div
        className="soccer-landing__photo soccer-landing__photo--stadium"
        style={{ backgroundImage: `url(${SOCCER_LANDING_IMAGES.stadiumBackground})` }}
        aria-hidden="true"
      />
      <div
        className="soccer-landing__photo soccer-landing__photo--team"
        style={{ backgroundImage: `url(${SOCCER_LANDING_IMAGES.teamBackground})` }}
        aria-hidden="true"
      />
      <div className="soccer-landing__gradient" aria-hidden="true" />
      <div className="soccer-landing__glow" aria-hidden="true" />
      <div className="soccer-landing__vignette" aria-hidden="true" />

      <div className="soccer-landing__card">
        <p className="soccer-landing__eyebrow">{SITE_NAME}</p>
        <h1>{SITE_APPS.soccer.name}</h1>
        <p className="soccer-landing__tagline">
          Clubs, practices, and a team divider that builds balanced sides for every session.
        </p>

        <ul className="soccer-landing__features">
          <li>Club management</li>
          <li>Availability voting</li>
          <li>Balanced team divider</li>
        </ul>

        <div className="soccer-landing__actions">
          {!isAuthenticated && !hasApprovedPlayer && !hasPendingPlayer && (
            <Link to="/register" className="btn btn-primary">
              Join {SITE_NAME}
            </Link>
          )}
          {isAuthenticated && !hasApprovedPlayer && !hasPendingPlayer && (
            <Link to={completeProfilePath()} className="btn btn-primary">
              Complete player profile
            </Link>
          )}
          {hasPendingPlayer && (
            <div className="soccer-landing__pending" style={{ width: '100%' }}>
              <strong>Player Registration Pending</strong>
              <p>
                Your registration is awaiting admin approval. You&apos;ll get full access once
                approved.
              </p>
            </div>
          )}
          {isAuthenticated && canRegisterClub && !checkingPlayer && (
            <Link to={soccerPath('/clubs/register')} className="btn btn-secondary">
              Register Club
            </Link>
          )}
        </div>

        {showQuickLinks && !checkingPlayer && (
          <nav className="soccer-landing__quick-links" aria-label="Quick navigation">
            <Link to={soccerPath('/clubs/list')}>Browse clubs</Link>
            <Link to="/training/soccer">Training</Link>
            {(hasApprovedPlayer || isAdmin() || isClubCreator()) && (
              <Link to={soccerPath('/players')}>Players</Link>
            )}
            {isAuthenticated && (isAdmin() || user?.roles?.includes('CLUB_ADMIN')) && (
              <Link to={soccerPath('/clubs')}>My clubs</Link>
            )}
          </nav>
        )}
      </div>

      {isAuthenticated && user?.id && hasApprovedPlayer && !checkingPlayer && (
        <div className="soccer-landing__dashboard-wrap">
          <TrainingTodayCard userId={user.id} />
          <PlayerHomeDashboard userId={user.id} limit={8} title="Your upcoming practices" />
        </div>
      )}
    </div>
  );
};

export default SoccerLanding;
